import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { produksjonAPI, Bemanning, TalentBehovItem } from '../services/api';
import { queryKeys } from '../lib/queryKeys';
import { erIkkeFunnet } from '../lib/erIkkeFunnet';

export type TalentBehov = TalentBehovItem;

/**
 * React Query hook for produksjonsdata
 * 
 * Erstatter gammel useState/useEffect pattern med React Query for:
 * - Automatisk caching
 * - Background refetching
 * - Optimistic updates
 * - Bedre feilhåndtering
 * 
 * @param id - Produksjon ID
 */
export const useProductionData = (id: string | undefined) => {
  const queryClient = useQueryClient();
  const prodId = id ? Number.parseInt(id, 10) : null;

  // Query: Hent produksjon
  const {
    data: produksjon = null,
    isLoading: produksjonLoading,
    error: produksjonError,
    refetch: refetchProduksjon,
  } = useQuery({
    queryKey: queryKeys.productions.detail(prodId!),
    queryFn: () => produksjonAPI.getById(prodId!).then(res => res.produksjon),
    enabled: !!prodId,
  });

  // Query: Hent bemanning
  const {
    data: bemanningData,
    isLoading: bemanningLoading,
  } = useQuery({
    queryKey: queryKeys.productions.bemanning(prodId!),
    queryFn: () => produksjonAPI.getBemanning(prodId!),
    enabled: !!prodId,
  });

  const bemanning = bemanningData?.bemanning || [];

  // Query: Hent talent-behov
  const {
    data: talentBehovData,
    isLoading: talentBehovLoading,
  } = useQuery({
    queryKey: queryKeys.productions.talentBehov(prodId!),
    queryFn: async () => {
      const data = await produksjonAPI.getTalentBehov(prodId!);
      // Normaliser data
      return (data.talentBehov || []).map(b => ({
        ...b,
        beskrivelse: b.beskrivelse ?? undefined,
      }));
    },
    enabled: !!prodId,
  });

  const talentBehov = talentBehovData || [];

  // Query: Hent plan-elementer
  const {
    data: planElementer = [],
    isLoading: planElementerLoading,
  } = useQuery({
    queryKey: queryKeys.productions.planElementer(prodId!),
    queryFn: () => produksjonAPI.getPlanElementer(prodId!).then(res => res.planElementer || []),
    enabled: !!prodId,
  });

  // Query: Hent produksjonsplan (hvis den finnes)
  const {
    data: plan = null,
  } = useQuery({
    queryKey: queryKeys.productionPlans.detail(produksjon?.plan_id!),
    queryFn: () => produksjonAPI.getPlan(produksjon!.plan_id!).then(res => res.plan),
    enabled: !!produksjon?.plan_id,
  });

  // Samlet loading state
  const loading = produksjonLoading || bemanningLoading || talentBehovLoading || planElementerLoading;
  
  // Samlet error state
  const error = produksjonError ? 'Kunne ikke laste produksjonsdata. Vennligst prøv igjen.' : null;
  // 404 og 403 er like for brukeren: «finnes ikke, eller du har ikke tilgang»
  const ikkeFunnet = erIkkeFunnet(produksjonError);

  // Hent produksjonen på nytt (knappen «Prøv igjen»)
  const refetch = () => {
    void refetchProduksjon();
  };

  // Refresh functions (invalidate queries)
  const refresh = async () => {
    if (!prodId) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.productions.detail(prodId) });
  };

  const refreshBemanning = async () => {
    if (!prodId) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.productions.bemanning(prodId) });
  };

  const refreshTalentBehov = async () => {
    if (!prodId) return;
    await queryClient.invalidateQueries({ queryKey: queryKeys.productions.talentBehov(prodId) });
  };

  // Beregn statistikk
  const bemanningStats = {
    totalt: bemanning.length,
    bekreftet: bemanning.filter(p => p.status === 'bekreftet').length,
    planlagt: bemanning.filter(p => p.status === 'planlagt').length,
    avvist: bemanning.filter(p => p.status === 'avvist').length,
    ikkeSvart: bemanning.filter(p => !p.status || p.status === 'ikke_svart').length,
  };

  // Grupper bemanning per talent kategori (øverste nivå)
  const bemanningPerKategori = bemanning.reduce((acc, person) => {
    const kategoriNavn = person.talent_kategori?.split(' → ')[0] || 'Ikke kategorisert';
    if (!acc[kategoriNavn]) {
      acc[kategoriNavn] = [];
    }
    acc[kategoriNavn].push(person);
    return acc;
  }, {} as Record<string, Bemanning[]>);

  return {
    produksjon,
    bemanning,
    talentBehov,
    plan,
    planElementer,
    loading,
    error,
    ikkeFunnet,
    refetch,
    bemanningStats,
    bemanningPerKategori,
    refresh,
    refreshBemanning,
    refreshTalentBehov,
  };
};

/**
 * Hook for å legge til bemanning med optimistic updates
 */
export const useAddBemanning = (produksjonId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { 
      personId: number; 
      talentNavn: string; 
      talentKategoriSti: string; 
      status?: 'planlagt' | 'bekreftet' | 'avvist'; 
      notater?: string;
    }) => {
      return await produksjonAPI.addBemanning(produksjonId, data);
    },
    onSuccess: () => {
      // Refetch bemanning
      queryClient.invalidateQueries({ queryKey: queryKeys.productions.bemanning(produksjonId) });
    },
  });
};

/**
 * Hook for å fjerne bemanning med optimistic updates
 */
export const useRemoveBemanning = (produksjonId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bemanningId: number) => {
      return await produksjonAPI.removeBemanning(produksjonId, bemanningId);
    },
    onMutate: async (bemanningId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.productions.bemanning(produksjonId) });

      // Snapshot previous value
      const previousBemanning = queryClient.getQueryData(queryKeys.productions.bemanning(produksjonId));

      // Optimistically remove
      queryClient.setQueryData(queryKeys.productions.bemanning(produksjonId), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          bemanning: old.bemanning.filter((b: Bemanning) => b.id !== bemanningId),
        };
      });

      return { previousBemanning };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousBemanning) {
        queryClient.setQueryData(
          queryKeys.productions.bemanning(produksjonId),
          context.previousBemanning
        );
      }
    },
    onSettled: () => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey: queryKeys.productions.bemanning(produksjonId) });
    },
  });
};
